import { Observable } from "rxjs";
import NetworkService from "../network/networkService";
import { IRegionProductSet, IProductSetsResponse } from "../models/IRegion.model";
import { URLConfig } from "../network/urlStore";

export interface ICreateProductSetRequest {
  regionId: number;
  name: string;
  cropCode: string;
}

export interface IUpdateProductSetRequest {
  name?: string;
  cropCode?: string;
}

export interface IPatchOperation {
  op: "replace" | "add" | "remove";
  path: string;
  value?: any;
}

export const getProductSetsByRegion = (
  regionId: number,
  seasonCode?: number
): Observable<IProductSetsResponse> => {
  let url = URLConfig.getProductSetsByRegionURL();
  let filter = `filter=region.id__eq:=${regionId}`;
  
  if (seasonCode) {
    filter += `;region.season__eq:=${seasonCode}`;
  }
  url += `?${filter}`;
  
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IProductSetsResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching Product Sets", error);
      }
    );
  });
};

export const getProductSetById = (
  productSetId: number
): Observable<IRegionProductSet> => {
  const url = URLConfig.getProductSetByIdURL(productSetId);
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IRegionProductSet;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching Product Set by ID", error);
      }
    );
  });
};

export const createProductSet = (
  payload: ICreateProductSetRequest
): Observable<IRegionProductSet> => {
  const url = URLConfig.createProductSetURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, payload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IRegionProductSet;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while creating Product Set", error);
      }
    );
  });
};

export const updateProductSet = (
  regionId: number,
  productSetId: number,
  payload: IUpdateProductSetRequest
): Observable<IRegionProductSet> => {
  const url = URLConfig.updateProductSetURL(regionId, productSetId);
  
  // Convert payload to JSON Patch format
  const patchOperations: IPatchOperation[] = [];
  
  if (payload.name !== undefined) {
    patchOperations.push({
      op: "replace",
      path: "/name",
      value: payload.name
    });
  }
  
  if (payload.cropCode !== undefined) {
    patchOperations.push({
      op: "replace",
      path: "/CropCode",
      value: payload.cropCode
    });
  }
  
  return new Observable((subscriber) => {
    NetworkService.patch(url, patchOperations).then(
      (res: unknown) => {
        // API might return empty response, still consider it success
        const response = res as unknown as IRegionProductSet;
        subscriber.next(response);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while updating Product Set", error);
      }
    );
  });
};

export const deleteProductSet = (
  regionId: number,
  productSetId: number
): Observable<void> => {
  const url = URLConfig.deleteProductSetURL(regionId, productSetId);
  return new Observable((subscriber) => {
    NetworkService.delete(url).then(
      (res: unknown) => {
        subscriber.next();
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while deleting Product Set", error);
      }
    );
  });
};

export const addProductsToProductSet = (
  productSetId: number,
  productIds: number[]
): Observable<IRegionProductSet> => {
  const url = URLConfig.updateProductSetURL(0, productSetId);
  
  // Create JSON Patch operations to add products
  const patchOperations: IPatchOperation[] = productIds.map(productId => ({
    op: "add",
    path: "/products/-",
    value: {
      productId: productId
    }
  }));
  
  return new Observable((subscriber) => {
    NetworkService.patch(url, patchOperations).then(
      (res: unknown) => {
        const response = res as unknown as IRegionProductSet;
        subscriber.next(response);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while adding products to Product Set", error);
      }
    );
  });
};

export const removeProductFromProductSet = (
  productSetId: number,
  productIndex: number
): Observable<IRegionProductSet> => {
  const url = URLConfig.updateProductSetURL(0, productSetId);
  
  // Create JSON Patch operation to remove product by index
  const patchOperations: IPatchOperation[] = [{
    op: "remove",
    path: `/products/${productIndex}`
  }];
  
  return new Observable((subscriber) => {
    NetworkService.patch(url, patchOperations).then(
      (res: unknown) => {
        const response = res as unknown as IRegionProductSet;
        subscriber.next(response);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while removing product from Product Set", error);
      }
    );
  });
};

export const addTreatmentProductsToProductSet = (
  productSetId: number,
  treatmentProducts: Array<{ trtProductId: number; name: string; unit: string; area: string }>
): Observable<IRegionProductSet> => {
  const url = URLConfig.updateProductSetURL(0, productSetId);
  
  // Create JSON Patch operations to add treatment products
  const patchOperations: IPatchOperation[] = treatmentProducts.map(treatment => ({
    op: "add",
    path: "/treatments/-",
    value: {
      trtProductId: treatment.trtProductId,
      name: treatment.name,
      unit: treatment.unit,
      area: treatment.area
    }
  }));
  
  return new Observable((subscriber) => {
    NetworkService.patch(url, patchOperations).then(
      (res: unknown) => {
        const response = res as unknown as IRegionProductSet;
        subscriber.next(response);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while adding treatment products to Product Set", error);
      }
    );
  });
};

export const removeTreatmentProductFromProductSet = (
  productSetId: number,
  treatmentProductIndex: number
): Observable<IRegionProductSet> => {
  const url = URLConfig.updateProductSetURL(0, productSetId);
  
  // Create JSON Patch operation to remove treatment product by index
  const patchOperations: IPatchOperation[] = [{
    op: "remove",
    path: `/treatments/${treatmentProductIndex}`
  }];
  
  return new Observable((subscriber) => {
    NetworkService.patch(url, patchOperations).then(
      (res: unknown) => {
        const response = res as unknown as IRegionProductSet;
        subscriber.next(response);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while removing treatment product from Product Set", error);
      }
    );
  });
};
